<script setup lang="ts">
import InputNumber from '@/components/BaseComponents/InputNumber.vue';
import { usePermissions } from '@/composables/usePermissions';
import type { MessageSchema } from '@/i18n';
import { useUserStore } from '@/stores/useUserStore';
import type { Dish } from '@/types/dish';
import type { CreateMenuDishRequest, MenuCategory, MenuDish, UpdateMenuDishRequest } from '@/types/menu';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import { useConfirm } from 'primevue/useconfirm';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const { t, te } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });
const { can } = usePermissions();
const confirm = useConfirm();
const router = useRouter();
const userStore = useUserStore();

interface Props {
  visible: boolean;
  dish?: MenuDish | null;
  categories: MenuCategory[];
  selectedCategoryId?: number;
  selectedDishFromRecipes?: Dish | null;
  selectedDishTitleFromRecipes?: string;
}

interface Emits {
  (e: 'update:visible', value: boolean): void;
  (e: 'save', data: (CreateMenuDishRequest | UpdateMenuDishRequest) & { categoryId: number }): void;
  (e: 'delete', dishId: number): void;
  (e: 'generate-from-title', data: { name: string; categoryId: number; dishId?: number }): void;
}

const props = withDefaults(defineProps<Props>(), {
  dish: null,
  selectedCategoryId: 0,
  selectedDishFromRecipes: null,
  selectedDishTitleFromRecipes: '',
});

const emit = defineEmits<Emits>();

const form = ref<(CreateMenuDishRequest | UpdateMenuDishRequest) & { id?: number; categoryId: number; dish_id?: number }>({
  name: '',
  description: '',
  price: undefined,
  order: undefined,
  categoryId: 0,
});

const isEditing = computed(() => !!props.dish);
const dialogTitle = computed(() => (isEditing.value ? t('menu.dish.edit.label') : t('menu.dish.add.label')));

const translatedCategories = computed(() => {
  return props.categories.map((category) => ({
    ...category,
    displayName: te(`menu.categories.${category.name}`) ? t(`menu.categories.${category.name}`) : category.name,
  }));
});

const canSave = computed(() => {
  const hasName = form.value.name?.trim();
  const hasCategory = form.value.categoryId;
  return hasName && hasCategory;
});

const canGenerateFromTitle = computed(() => {
  const hasName = form.value.name?.trim();
  const hasCategory = form.value.categoryId;
  // Only allow generation when editing an existing dish (not creating new)
  const isEditingExistingDish = isEditing.value;
  // Don't allow generation if this is from recipes or already has a dish_id (meaning it's already a generated/real dish)
  const noDishFromRecipes = !props.selectedDishFromRecipes && !props.selectedDishTitleFromRecipes;
  const notAlreadyGenerated = !form.value.dish_id;
  const hasPermission = can('dishes', 'create');

  return hasName && hasCategory && isEditingExistingDish && noDishFromRecipes && notAlreadyGenerated && hasPermission;
});

const isNameDisabled = computed(
  () => !!(props.selectedDishFromRecipes || props.selectedDishTitleFromRecipes || (isEditing.value && form.value.dish_id)),
);

const showDishSourceInfo = computed(() => !!(props.selectedDishFromRecipes || props.selectedDishTitleFromRecipes));

// Watch for dish changes to populate form
watch(
  () => props.dish,
  (newDish) => {
    if (newDish) {
      // Find the category this dish belongs to
      const dishCategory = props.categories.find((cat) => cat.dishes?.some((d) => d.id === newDish.id));

      form.value = {
        id: newDish.id,
        dish_id: newDish.dish_id,
        name: newDish.name || newDish.dish?.name || '',
        description: newDish.description || '',
        price: newDish.price,
        order: newDish.order,
        categoryId: dishCategory?.id || 0,
      };
    } else {
      // Reset form for new dish
      form.value = {
        name: props.selectedDishFromRecipes?.name || props.selectedDishTitleFromRecipes || '',
        description: '',
        price: undefined,
        order: undefined,
        categoryId: props.selectedCategoryId || 0,
      };
    }
  },
  { immediate: true },
);

// Watch for selected dish from recipes
watch([() => props.selectedDishFromRecipes, () => props.selectedDishTitleFromRecipes], () => {
  if (!isEditing.value) {
    form.value.name = props.selectedDishFromRecipes?.name || props.selectedDishTitleFromRecipes || '';
  }
});

// Watch for selected category
watch(
  () => props.selectedCategoryId,
  (newCategoryId) => {
    if (!isEditing.value && newCategoryId) {
      form.value.categoryId = newCategoryId;
    }
  },
);

// Reset form when dialog closes
watch(
  () => props.visible,
  (isVisible) => {
    if (!isVisible && !isEditing.value) {
      form.value = {
        name: '',
        description: '',
        price: undefined,
        order: undefined,
        categoryId: 0,
      };
    }
  },
);

const handleGenerateDishFromTitle = async () => {
  if (!canGenerateFromTitle.value) return;
  if (!userStore.user?.restaurants?.[0]?.id) return;
  if (!form.value.name?.trim()) return;

  if (!userStore.validateUserCanGenerate()) {
    return;
  }

  // Emit the generation request to the parent component
  emit('generate-from-title', {
    name: form.value.name.trim(),
    categoryId: form.value.categoryId,
    dishId: isEditing.value ? form.value.id : undefined,
  });

  // Close the dialog immediately
  emit('update:visible', false);
};

const handleSave = () => {
  if (!canSave.value) return;

  const saveData = { ...form.value };

  // If we have a selected dish from recipes, include its ID
  if (props.selectedDishFromRecipes && !isEditing.value) {
    saveData.dish_id = props.selectedDishFromRecipes.id;
  }

  if (isEditing.value) {
    const { categoryId, ...dishData } = saveData;
    const updateData = {
      ...dishData,
      menu_category_id: categoryId,
      categoryId, // Keep for component logic
    };
    emit('save', updateData as UpdateMenuDishRequest & { categoryId: number });
  } else {
    const { ...createData } = saveData;
    emit('save', createData as CreateMenuDishRequest & { categoryId: number });
  }
};

const handleDelete = () => {
  if (!props.dish || !can('menus', 'delete')) return;

  const dishName = props.dish.name || props.dish.dish?.name || 'Unknown';

  confirm.require({
    message: `${t('menu.dish.delete.confirm')} "${dishName}"?`,
    header: t('menu.dish.delete.confirm'),
    icon: 'pi pi-exclamation-triangle',
    rejectClass: 'p-button-secondary p-button-outlined',
    rejectLabel: t('common.cancel'),
    acceptLabel: t('common.delete'),
    accept: () => {
      if (props.dish) {
        emit('delete', props.dish.id);
      }
    },
  });
};

const handleCancel = () => {
  emit('update:visible', false);
};

const redirectToDish = (dishId: number) => {
  router.push({ path: `/menu/dishes/${dishId}` });
};
</script>

<template>
  <Dialog
    :visible="visible"
    modal
    :header="dialogTitle"
    :style="{ width: '30rem', maxWidth: '90vw' }"
    @update:visible="emit('update:visible', $event)"
  >
    <div v-if="visible" class="space-y-4">
      <!-- Show dish source info if from recipes -->
      <div v-if="showDishSourceInfo" class="rounded-lg bg-blue-50 p-3">
        <p class="text-sm text-blue-700">
          <i class="pi pi-info-circle mr-2"></i>
          {{ t('menu.dish.addFromRecipesInfo') }}
        </p>
      </div>

      <!-- Add dish link if dish_id exists in edit mode -->
      <div v-if="isEditing && form.dish_id" class="hover:cursor-pointer">
        <div class="cursor-pointer text-sm italic text-primary-600 hover:text-primary-700" @click="redirectToDish(form.dish_id)">
          <i class="pi pi-arrow-up-right mr-1"></i>
          {{ t('menu.dish.viewOriginalDish') }}
        </div>
      </div>

      <!-- Category Selection -->
      <div v-if="categories.length > 0" class="flex items-center gap-4">
        <label for="categorySelect" class="w-24 font-semibold">{{ t('menu.category.label') }}</label>
        <Dropdown
          id="categorySelect"
          v-model="form.categoryId"
          :options="translatedCategories"
          option-label="displayName"
          option-value="id"
          :placeholder="t('menu.category.select')"
          class="flex-auto"
        />
      </div>

      <div class="flex items-start gap-4">
        <label for="dishName" class="w-24 font-semibold">{{ t('menu.dish.form.name.label') }}</label>
        <div class="flex flex-1 flex-col gap-2">
          <InputText
            id="dishName"
            v-model="form.name"
            :disabled="isNameDisabled"
            :placeholder="t('menu.dish.form.name.placeholder')"
            :class="{ 'cursor-not-allowed opacity-50': isNameDisabled }"
            class="flex-auto"
            autocomplete="off"
          />
          <!-- Generate button -->
          <div class="flex w-full items-end">
            <Button
              v-if="canGenerateFromTitle"
              type="button"
              :label="t('common.generate')"
              :disabled="false"
              size="small"
              outlined
              @click="handleGenerateDishFromTitle"
            />
          </div>
        </div>
      </div>

      <!-- Show info message when name is disabled in edit mode -->
      <div v-if="isEditing && form.dish_id" class="-mt-2 text-xs text-gray-600">
        <i class="pi pi-info-circle mr-1"></i>
        {{ t('menu.dish.edit.nameFromOriginalDish') }}
      </div>

      <div class="flex items-center gap-4">
        <label for="dishDescription" class="w-24 font-semibold">{{ t('menu.dish.form.description.label') }}</label>
        <InputText
          id="dishDescription"
          v-model="form.description"
          class="flex-auto"
          :placeholder="t('menu.dish.form.description.placeholder')"
          autocomplete="off"
        />
      </div>

      <div class="flex items-center gap-4">
        <label for="dishPrice" class="w-24 font-semibold">{{ t('menu.dish.form.price.label') }}</label>
        <InputNumber
          id="dishPrice"
          v-model="form.price"
          class="flex-auto"
          mode="currency"
          currency="CHF"
          :min-fraction-digits="2"
          :placeholder="isEditing ? undefined : t('menu.dish.form.price.optional')"
        />
      </div>
    </div>

    <div class="mt-6 flex justify-between gap-2">
      <Button
        v-if="isEditing && can('menus', 'delete')"
        type="button"
        :label="t('common.delete')"
        severity="danger"
        outlined
        :disabled="false"
        @click="handleDelete"
      />
      <div class="flex justify-end gap-2" :class="{ 'ml-auto': !isEditing || !can('menus', 'delete') }">
        <Button type="button" :label="t('common.cancel')" outlined :disabled="false" @click="handleCancel" />
        <Button type="button" :label="t('common.save')" :disabled="!canSave" @click="handleSave" />
      </div>
    </div>
  </Dialog>
</template>
